import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Play } from "lucide-react";
import { VIDEO_REELS } from "@/lib/data";

export default function Video() {
    const [open, setOpen] = useState(null);
    const films = VIDEO_REELS.filter((v) => v.kind === "Film");
    const reels = VIDEO_REELS.filter((v) => v.kind === "Reel");

    return (
        <div className="pt-24 sm:pt-28">
            <Section spacing="tight">
                <Kicker>Cinema</Kicker>
                <SerifTitle className="mt-3">Films &amp; reels, composed like photographs.</SerifTitle>
                <p className="mt-5 max-w-[60ch] text-sm leading-[1.85] text-muted-foreground">
                    Cinematic brand films, founder stories, event highlights, and short‑form social reels. Added to a session, or commissioned as a stand‑alone production.
                </p>
            </Section>

            <Section spacing="normal">
                <Tabs defaultValue="films" className="w-full" data-testid="video-gallery">
                    <TabsList className="bg-transparent gap-2 p-0 mb-8">
                        <TabsTrigger value="films" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-5 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Films
                        </TabsTrigger>
                        <TabsTrigger value="reels" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-5 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Reels
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="films">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {films.map((v) => (
                                <button key={v.id} data-testid="video-gallery-item" onClick={() => setOpen(v)} className="text-left group">
                                    <EditorialImage
                                        src={v.thumb}
                                        alt={v.title}
                                        ratio={16 / 9}
                                        overlay={
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="h-14 w-14 rounded-full bg-[hsl(var(--lux-porcelain))]/85 backdrop-blur flex items-center justify-center">
                                                    <Play className="h-5 w-5" />
                                                </div>
                                            </div>
                                        }
                                    />
                                    <div className="mt-3 flex items-center justify-between text-xs">
                                        <span className="font-serif-display text-lg">{v.title}</span>
                                        <span className="text-muted-foreground tracking-[0.16em] uppercase">{v.duration}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </TabsContent>
                    <TabsContent value="reels">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {reels.map((v) => (
                                <button key={v.id} data-testid="video-gallery-item" onClick={() => setOpen(v)} className="text-left group">
                                    <EditorialImage
                                        src={v.thumb}
                                        alt={v.title}
                                        ratio={9 / 16}
                                        overlay={
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="h-12 w-12 rounded-full bg-[hsl(var(--lux-porcelain))]/85 backdrop-blur flex items-center justify-center">
                                                    <Play className="h-4 w-4" />
                                                </div>
                                            </div>
                                        }
                                    />
                                    <div className="mt-2 flex items-center justify-between text-[11px]">
                                        <span>{v.title}</span>
                                        <span className="text-muted-foreground">{v.duration}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </Section>

            <Section spacing="normal" className="text-center bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <SerifTitle className="max-w-[22ch] mx-auto">Add video to your session.</SerifTitle>
                <p className="mt-5 text-sm text-muted-foreground max-w-[56ch] mx-auto">
                    Short‑form reels for social, founder films for the website, or cinematic event highlights — video is offered as an add‑on to most sessions, or as a stand‑alone production.
                </p>
                <Link to="/inquire?type=Photo%20%2B%20Video%20Package" className="btn-request mt-7 inline-flex">
                    Request Availability
                </Link>
            </Section>

            <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
                <DialogContent className="max-w-[1100px] w-[96vw] bg-[hsl(var(--lux-ink))] text-[hsl(var(--lux-porcelain))] border-none p-0">
                    {open && (
                        <div className="p-6">
                            <div className="font-serif-display text-2xl">{open.title}</div>
                            <div className="kicker mt-1 text-[hsl(var(--lux-porcelain))]/70">{open.kind} · {open.duration}</div>
                            <div className="mt-4 aspect-video bg-black flex items-center justify-center">
                                <div className="text-[hsl(var(--lux-porcelain))]/60 text-sm">Film preview coming soon</div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
